import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useCallback } from 'react';
import { authApi } from './api';
import { queryKeys } from './query-keys';
import { sessionQueryOptions } from './session-query';

/**
 * Auth state hook.
 *
 * Wraps the shared `sessionQueryOptions` so components and `beforeLoad`
 * router guards all query the exact same cache entry. Exposes a typed
 * view (`user`, `isLoggedIn`, `isLoggedOut`) and a `logout()` that clears
 * the server session, empties the session cache entry, then wipes the
 * hills and GitLab caches to avoid cross-user leaks when the next user
 * logs in on the same tab.
 */
export function useSession() {
  const qc = useQueryClient();
  const query = useQuery(sessionQueryOptions());

  const logout = useCallback(async () => {
    await authApi.logout();
    qc.setQueryData(queryKeys.session(), null);
    // Evict everything keyed by the session: hill list, hill details,
    // snapshots, importable GitLab projects. Next session starts clean.
    qc.removeQueries({ queryKey: ['hills'] });
    qc.removeQueries({ queryKey: ['gitlab'] });
  }, [qc]);

  return {
    user: query.data ?? null,
    isLoading: query.isLoading,
    isLoggedIn: query.data !== null && query.data !== undefined,
    isLoggedOut: !query.isLoading && (query.data === null || query.data === undefined),
    error: query.error,
    logout,
  };
}
